import { Collapse, Divider } from '@material-ui/core'
import { BaseTheme, Icon, styled, Typography } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { useFeatureFlag } from 'util/featureFlags'

const getScrollableAncestor = (element: HTMLElement | null): HTMLElement | null => {
  let parent = element?.parentElement ?? null
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent)
    if (overflowY === 'auto' || overflowY === 'scroll') return parent
    parent = parent.parentElement
  }
  return null
}

const FULL_DISCLAIMER_TEXT =
  'OpenSolar operates as a marketplace through which third-party distributors list, price and supply products directly to installers. All prices and offers are set by the named distributor. Where reference is made to a previous price or saving, this reflects pricing determined by the distributor and may be based on prices at which the distributor has offered the product for sale within the OpenSolar marketplace and/or through its own direct sales channels or other third-party platforms, as determined by the distributor. A referenced previous price may not necessarily have been available within the OpenSolar Shop itself. If you are uncertain as to the nature or basis of any saving, you should contact the distributor directly before making a purchase.'

const SHORT_DISCLAIMER_TEXT = 'Prices and offers on this page are set by individual suppliers, not OpenSolar.'

const Wrapper = styled('div', {
  name: 'MarketplaceDisclaimerBanner',
})({
  width: '100%',
})

const Card = styled('div')<{ isFullWidth: boolean; showBorder: boolean }>(({ theme, isFullWidth, showBorder }) => ({
  backgroundColor: theme.palette.common.white,
  border: showBorder ? `1px solid ${theme.palette.grey[200]}` : 'none',
  borderRadius: isFullWidth ? 0 : 8,
  padding: `${isFullWidth ? '16px 24px' : '16px'}`,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 16,
  width: '100%',
  boxSizing: 'border-box',
}))

const IconWrapper = styled('div')(({ theme }) => ({
  flexShrink: 0,
  marginTop: 1,
  height: 36,
  width: 36,
  backgroundColor: theme.palette.blue[300],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
}))

const ContentWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
})

const TitleRow = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
})

const ToggleLink = styled('span')(({ theme }) => ({
  color: theme.palette.blue[900],
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  flexShrink: 0,
  fontSize: 12,
  lineHeight: '16px',
  '&:hover': {
    textDecoration: 'underline',
  },
}))

interface MarketplaceDisclaimerBannerProps {
  isFullWidth?: boolean
  showIcon?: boolean
  showBorder?: boolean
  showTitle?: boolean
  scrollIntoViewOnExpand?: boolean
}

const MarketplaceDisclaimerBanner = ({
  isFullWidth = false,
  showIcon = true,
  showBorder = false,
  showTitle = true,
  scrollIntoViewOnExpand = false,
}: MarketplaceDisclaimerBannerProps) => {
  const translate = useTranslate()
  const [isExpanded, setIsExpanded] = useState(false)
  const expandedContentId = useId()
  const bannerRef = useRef<HTMLDivElement>(null)
  const enableMultiDistributorShop = useFeatureFlag('enable_multi_distributor_shop', 'on')

  const scrollBannerIntoView = useCallback(() => {
    const banner = bannerRef.current
    if (!banner) return

    banner.scrollIntoView({ block: 'end', behavior: 'auto' })

    requestAnimationFrame(() => {
      const scrollParent = getScrollableAncestor(banner)
      if (!scrollParent) return

      const overflow = banner.getBoundingClientRect().bottom - scrollParent.getBoundingClientRect().bottom
      if (overflow > 0) scrollParent.scrollTop += overflow
    })
  }, [])

  useEffect(() => {
    if (!scrollIntoViewOnExpand || !isExpanded) return

    scrollBannerIntoView()

    const banner = bannerRef.current
    if (!banner) return

    const resizeObserver = new ResizeObserver(() => {
      scrollBannerIntoView()
    })
    resizeObserver.observe(banner)

    return () => resizeObserver.disconnect()
  }, [isExpanded, scrollBannerIntoView, scrollIntoViewOnExpand])

  const handleToggleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setIsExpanded((prev) => !prev)
    }
  }

  if (!enableMultiDistributorShop) return null

  return (
    <Wrapper ref={bannerRef}>
      <Card isFullWidth={isFullWidth} showBorder={showBorder}>
        {showIcon && (
          <IconWrapper>
            <Icon icon="storefront" size="small" color={BaseTheme.palette.blue[900]} />
          </IconWrapper>
        )}
        <ContentWrapper>
          <TitleRow>
            {showTitle && (
              <Typography textVariant="body2Medium" style={{ color: BaseTheme.palette.neutralGrey[1000] }}>
                {translate('Marketplace Pricing Notice')}
              </Typography>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Typography
                textVariant="body2"
                style={{
                  fontSize: 12,
                  lineHeight: '16px',
                  color: BaseTheme.palette.neutralGrey[1000],
                  flexShrink: 1,
                }}
              >
                {translate(SHORT_DISCLAIMER_TEXT)}
              </Typography>
              <ToggleLink
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
                aria-controls={expandedContentId}
                onClick={() => setIsExpanded((prev) => !prev)}
                onKeyDown={handleToggleKeyDown}
              >
                {isExpanded ? translate('Show less') : translate('Read full disclaimer')}
              </ToggleLink>
            </div>
          </TitleRow>
          <Collapse in={isExpanded} timeout={200}>
            <div id={expandedContentId}>
              <Divider style={{ margin: '12px 0' }} />
              <Typography
                textVariant="body3Medium"
                style={{
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: '16px',
                  letterSpacing: 0,
                  color: BaseTheme.palette.neutralGrey[900],
                }}
              >
                {translate(FULL_DISCLAIMER_TEXT)}
              </Typography>
            </div>
          </Collapse>
        </ContentWrapper>
      </Card>
    </Wrapper>
  )
}

export default MarketplaceDisclaimerBanner
